import React, {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { getSubscriptionToken } from '@/lib/centrifugoClient';

const CentrifugoContext = createContext(null);
const CentrifugoChannelContext = createContext(null);

export const useCentrifugo = () => useContext(CentrifugoContext);
export const useCentrifugoSubscription = () =>
	useContext(CentrifugoChannelContext);

export const CentrifugoProvider = ({ client, children }) => (
	<CentrifugoContext.Provider value={client}>
		{children}
	</CentrifugoContext.Provider>
);

export const CentrifugoChannelProvider = (props) => {
	const { channelName, subscriptionTokenType, children } = props;
	const centrifuge = useCentrifugo();
	const [subscription, setSubscription] = useState(null);

	useEffect(() => {
		if (!centrifuge || !channelName || !subscriptionTokenType) {
			return undefined;
		}
		const sub = centrifuge.newSubscription(channelName, {
			getToken: () => getSubscriptionToken(channelName, subscriptionTokenType),
		});
		sub.subscribe();
		setSubscription(sub);
		return () => {
			sub.unsubscribe();
			centrifuge.removeSubscription(sub);
		};
	}, [centrifuge, channelName, subscriptionTokenType]);

	return (
		<CentrifugoChannelContext.Provider value={subscription}>
			{children}
		</CentrifugoChannelContext.Provider>
	);
};

// Registers a handler for publications on the enclosing CentrifugoChannelProvider.
// The handler receives the publication body: { event, payload }.
export const useCentrifugoChannel = (onPublication) => {
	const subscription = useCentrifugoSubscription();
	const handlerRef = useRef(onPublication);
	handlerRef.current = onPublication;

	useEffect(() => {
		if (!subscription) {
			return undefined;
		}
		const callback = (ctx) => handlerRef.current(ctx.data, ctx);
		subscription.on('publication', callback);
		return () => {
			subscription.off('publication', callback);
		};
	}, [subscription]);
};
